import { Directive, ElementRef, Input, OnDestroy, afterNextRender, inject, numberAttribute } from '@angular/core';
import { gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

/**
 * 3D tilt toward the pointer, desktop fine pointers only (no gyroscope). The directive only
 * writes CSS variables (--rx, --ry, --gx, --gy); motion/base.css turns them into the transform
 * and the optional `.tilt-glare` highlight.
 */
@Directive({ selector: '[appTilt]' })
export class TiltDirective implements OnDestroy {
  /** Maximum rotation in degrees. */
  @Input({ transform: numberAttribute }) tiltMax = 7;
  @Input({ transform: numberAttribute }) tiltScale = 1.02;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  constructor() {
    this.el.classList.add('dv-tilt');
    afterNextRender(() => this.motion.afterPaint(() => this.destroyed || this.setup()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  private setup(): void {
    const el = this.el;
    el.style.setProperty('--ts', String(this.tiltScale));
    const mm = (this.mm = gsap.matchMedia(el));
    mm.add({ motion: QUERIES.motion, fine: QUERIES.fine }, (ctx) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion'] || !conditions['fine']) return;

      let frame = 0;
      let x = 0;
      let y = 0;
      const write = () => {
        frame = 0;
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const nx = ((x - r.left) / r.width) * 2 - 1;
        const ny = ((y - r.top) / r.height) * 2 - 1;
        el.style.setProperty('--ry', `${(nx * this.tiltMax).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${(-ny * this.tiltMax).toFixed(2)}deg`);
        el.style.setProperty('--gx', `${(nx * 25).toFixed(1)}%`);
        el.style.setProperty('--gy', `${(ny * 25).toFixed(1)}%`);
      };
      const onEnter = () => el.classList.add('is-tilting');
      const onMove = (e: PointerEvent) => {
        x = e.clientX;
        y = e.clientY;
        if (!frame) frame = requestAnimationFrame(write);
      };
      const onLeave = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        el.classList.remove('is-tilting');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      };

      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      return () => {
        el.removeEventListener('pointerenter', onEnter);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
        if (frame) cancelAnimationFrame(frame);
        el.classList.remove('is-tilting');
        for (const name of ['--rx', '--ry', '--gx', '--gy']) el.style.removeProperty(name);
      };
    });
  }
}
